import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { X, RotateCcw, ArrowRight, Play, Pause, Volume2, VolumeX, Sparkles } from 'lucide-react';
import { getFunnelSessionId, logFunnel } from '../lib/funnel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const logStoryEvent = (event, slot, extra = {}) => {
  if (!BACKEND_URL) return;
  const payload = {
    event,
    slot,
    session_id: getFunnelSessionId(),
    ...extra,
  };
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (navigator.sendBeacon && navigator.sendBeacon(`${BACKEND_URL}/api/analytics/success-story-event`, blob)) return;
  } catch { /* fall through */ }
  fetch(`${BACKEND_URL}/api/analytics/success-story-event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
};

const absoluteUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

// Lightweight placeholder shown when no MP4 / external URL is uploaded yet.
// Keeps the marketing layout polished while waiting for production assets.
const PlaceholderPoster = ({ story }) => (
  <div className={`absolute inset-0 bg-gradient-to-br ${story.accent || 'from-slate-700 to-slate-900'} flex flex-col items-center justify-center text-white p-6 text-center`}>
    <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur border border-white/25 flex items-center justify-center mb-4">
      <Sparkles className="w-6 h-6 text-white" />
    </div>
    <div className="text-xs font-bold uppercase tracking-widest text-white/70 mb-2">Coming Soon</div>
    <div className="text-xl sm:text-2xl font-bold font-['Outfit'] max-w-md leading-tight">{story.title}</div>
    <div className="text-sm text-white/80 mt-2 max-w-md italic">&ldquo;{story.tagline}&rdquo;</div>
    <div className="text-[11px] text-white/60 mt-6 max-w-md leading-relaxed">{story.description}</div>
  </div>
);

export default function SuccessStoryModal({ story, onClose }) {
  const videoRef = useRef(null);
  const completedRef = useRef(false);
  const ctaClickedRef = useRef(false);
  const elapsedRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [hasError, setHasError] = useState(false);

  const mp4Src = absoluteUrl(story.video_url);
  const embedUrl = story.embed_url;  // pre-built embed URL from backend
  const useMp4 = !!mp4Src;
  const useEmbed = !mp4Src && !!embedUrl;
  const useFallback = !mp4Src && !embedUrl;

  // Fire analytics
  useEffect(() => {
    logStoryEvent('opened', story.slot, { source: useMp4 ? 'mp4' : useEmbed ? 'embed' : 'placeholder' });
    logFunnel('demo_opened', { slot: story.slot, kind: 'success_story' });
    return () => {
      if (!completedRef.current && !ctaClickedRef.current && useMp4) {
        const vid = videoRef.current;
        const seconds = vid ? Math.round(vid.currentTime * 10) / 10 : elapsedRef.current;
        logStoryEvent('closed_early', story.slot, { seconds_watched: seconds });
      }
    };
  }, [story.slot]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Auto-play MP4 on mount, muted to satisfy browser autoplay rules.
  useEffect(() => {
    if (!useMp4) return;
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = true;
    setIsMuted(true);
    vid.play().catch(() => { setIsPlaying(false); });
  }, [useMp4]);

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      vid.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) {
      setIsMuted((m) => !m);
      return;
    }
    vid.muted = !vid.muted;
    setIsMuted(vid.muted);
  };

  const replay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.currentTime = 0;
    vid.play().then(() => setIsPlaying(true)).catch(() => {});
    completedRef.current = false;
  };

  const handleEnded = () => {
    if (!completedRef.current) {
      completedRef.current = true;
      logStoryEvent('completed', story.slot);
      logFunnel('demo_completed', { slot: story.slot, kind: 'success_story' });
    }
    setIsPlaying(false);
  };

  const handleTimeUpdate = (e) => {
    elapsedRef.current = e.target.currentTime || 0;
  };

  const handleCta = () => {
    if (!ctaClickedRef.current) {
      ctaClickedRef.current = true;
      logStoryEvent('cta_clicked', story.slot);
      logFunnel('hero_cta_clicked', { location: 'success_story', slot: story.slot });
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/85 backdrop-blur-md p-3 sm:p-6 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="success-story-modal-overlay"
    >
      <div
        className="relative bg-slate-950 rounded-2xl shadow-2xl border border-white/10 w-full max-w-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid={`success-story-modal-${story.slot}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-slate-900/80">
          <div className="min-w-0">
            <div className="text-white text-sm font-bold font-['Outfit'] truncate">{story.title}</div>
            <div className="text-white/40 text-[10px] truncate">{story.tagline}</div>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors shrink-0"
            data-testid="success-story-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Player */}
        <div className="relative aspect-video bg-black overflow-hidden" data-testid="success-story-player">
          {useMp4 && !hasError && (
            <>
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                src={mp4Src}
                poster={absoluteUrl(story.poster_url) || undefined}
                playsInline
                onEnded={handleEnded}
                onTimeUpdate={handleTimeUpdate}
                onError={() => setHasError(true)}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                data-testid="success-story-video"
              />
              {/* Custom mute / play controls overlay */}
              <div className="absolute top-3 right-3 z-30 flex gap-2">
                <button
                  onClick={toggleMute}
                  className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white border border-white/10 transition-colors"
                  data-testid="success-story-mute-btn"
                  aria-label="Toggle mute"
                >
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={togglePlay}
                  className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white border border-white/10 transition-colors"
                  data-testid="success-story-play-btn"
                  aria-label="Play/Pause"
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </button>
                <button
                  onClick={replay}
                  className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white border border-white/10 transition-colors"
                  data-testid="success-story-replay-btn"
                  aria-label="Replay"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {useEmbed && (
            <iframe
              src={embedUrl}
              title={story.title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              onLoad={() => { /* External player owns its own state */ }}
              data-testid="success-story-iframe"
            />
          )}

          {(useFallback || hasError) && (
            <PlaceholderPoster story={story} />
          )}
        </div>

        {/* Description + CTA */}
        <div className="bg-slate-900/90 border-t border-white/10 px-4 sm:px-5 py-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-white/90 text-sm font-semibold leading-snug">
              {story.tagline}
            </div>
            <div className="text-white/50 text-xs mt-1 leading-relaxed line-clamp-2">
              {story.description}
            </div>
          </div>
          <div className="shrink-0">
            <Link to={`/register?ref=story-${story.slot}`} onClick={handleCta}>
              <Button
                size="sm"
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:opacity-90 text-white gap-1.5 font-semibold w-full sm:w-auto"
                data-testid={`success-story-cta-${story.slot}`}
              >
                Sign Up Free
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
